import type { DateFormat } from "./date-format";

/**
 * Formats a YYYY-MM-DD date part string for display in an input according to the user's format.
 * Avoids new Date() to sidestep timezone-offset issues.
 */
export function formatDateInput(isoDatePart: string, format: DateFormat): string {
  const parts = isoDatePart.split("-");
  if (parts.length !== 3) return isoDatePart;
  const [year, month, day] = parts;
  switch (format) {
    case "DD/MM/YYYY": return `${day}/${month}/${year}`;
    case "MM/DD/YYYY": return `${month}/${day}/${year}`;
    default:           return `${year}-${month}-${day}`;
  }
}

/**
 * Formats a YYYY-MM-DD date part for display, or "N/A" when there is no date to show.
 *
 * `formatDateInput` returns its input unchanged on anything that does not split into
 * three parts, which is right for an input field being typed into but renders an empty
 * string where a reader expects a date — leaving a bare separator next to it. Display
 * sites use this instead, matching what `formatDate`/`formatTimeOfDay` already return.
 */
export function formatDateDisplay(isoDatePart: string | null | undefined, format: DateFormat): string {
  if (!isoDatePart || isoDatePart.split("-").length !== 3) return "N/A";
  return formatDateInput(isoDatePart, format);
}

// The timezone the backend reads a bare date in: `common_core.validator.datetime.Date`
// anchors one to midnight `America/Sao_Paulo`. Anything compared against a date filter has
// to resolve in the same zone, or the date a row shows and the date the filter selects on
// disagree for any viewer outside it.
export const FILTER_TIME_ZONE = "America/Sao_Paulo";

// `en-CA` is the locale whose short date format is ISO `YYYY-MM-DD`, which is what
// `formatDateDisplay` consumes.
const isoDateInZone = new Intl.DateTimeFormat("en-CA", {
  timeZone: FILTER_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * The calendar date an instant falls on in the zone date filters are read in.
 *
 * `formatDate` resolves through the *viewer's* zone, which is right for a timestamp read on
 * its own and wrong for one the user is about to type into a date filter one card away: east
 * of São Paulo an instant late in the UTC day displays as the following date, and filtering
 * on the date just read then excludes the row that was read.
 */
export function formatDateInFilterZone(
  instant: string | null | undefined,
  format: DateFormat,
): string {
  return formatDateDisplay(calendarDayInFilterZone(instant), format);
}

/**
 * The bare `YYYY-MM-DD` calendar day an instant falls on in the zone date filters are read
 * in, or `null` when `instant` is missing or unparseable.
 *
 * A day-bound filter (`investors/detail.tsx`'s `startDate`/`endDate`) compares against this,
 * never against the instant string itself: `"2026-08-20T13:05:00+00:00" > "2026-08-20"` is
 * true under a plain string compare, because the longer string is a superstring of the
 * bound, so a same-day trade with any time-of-day past midnight silently fails an end-date
 * bound that should include it. Comparing two bare `YYYY-MM-DD` strings sorts correctly
 * because same-length ISO dates compare lexicographically in calendar order.
 */
export function calendarDayInFilterZone(instant: string | null | undefined): string | null {
  if (!instant) return null;
  const parsed = new Date(instant);
  if (isNaN(parsed.getTime())) return null;
  return isoDateInZone.format(parsed);
}

/**
 * The zone's UTC offset at `instant`, as `+HH:MM` / `-HH:MM`.
 *
 * Read from an instant rather than hardcoded: Brazil observed DST until 2019, so a date
 * before then is legitimately -02:00.
 */
function zoneOffsetAt(instant: Date): string {
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: FILTER_TIME_ZONE,
    timeZoneName: "longOffset",
  })
    .formatToParts(instant)
    .find(part => part.type === "timeZoneName")?.value;
  return (label || "").replace("GMT", "") || "+00:00";
}

/**
 * True when year/month/day form a real calendar date.
 *
 * `Date.UTC` maps years 0-99 onto 1900+year, which would read the date from the wrong
 * century — build in a safe year and set the real one with `setUTCFullYear`, which has no
 * such remapping. `Date.UTC` also rolls an impossible date over rather than rejecting it
 * (Feb 30 -> Mar 1), so reading the parts back is the only way to know the input was a real
 * calendar date. Shared by every calendar-date check below — an inline copy of this probe
 * has drifted before by dropping the century guard.
 */
function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (![year, month, day].every(part => Number.isInteger(part))) return false;
  const probe = new Date(Date.UTC(2000, month - 1, day, 12));
  probe.setUTCFullYear(year);
  if (isNaN(probe.getTime())) return false;
  return (
    probe.getUTCFullYear() === year
    && probe.getUTCMonth() === month - 1
    && probe.getUTCDate() === day
  );
}

/**
 * True when `value` is a real `YYYY-MM-DD` calendar date. `DateField` already constrains
 * what it emits to this shape, but a cleared field reports an empty string, not an absent
 * one — this is the check that turns that into a field error instead of a request
 * carrying no date at all.
 */
export function isValidCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  return isRealCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]));
}

/**
 * The last instant of `isoDatePart` in the zone date filters are read in, as an ISO string,
 * or `null` when the input is not a real calendar date.
 *
 * A bare `YYYY-MM-DD` sent as an upper bound means *midnight* of that day, so a control
 * labelled "granted until 13 Aug" that sends the bare date excludes all of 13 Aug bar its
 * first instant — and an equal from/until pair returns nothing. Lower bounds need no such
 * translation: midnight is already the right edge for "from".
 *
 * Returns `null` rather than throwing or emitting a half-parsed string. This runs inside a
 * `useMemo` during render, where a throw has no `try` and no error boundary above it, and a
 * bound the backend would reject turns a filter into an error card. A caller handed `null`
 * drops the bound instead of sending one it could not compose.
 */
export function endOfDayInFilterZone(isoDatePart: string): string | null {
  const parts = isoDatePart.split("-");
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(Number);
  if (!isRealCalendarDate(year, month, day)) return null;

  // Probed at midday UTC, which under this zone's -02:00/-03:00 offsets is mid-morning
  // local: inside the day and far from any transition. That is a property of
  // FILTER_TIME_ZONE, not a general one — a zone near the date line would need the probe
  // moved into the zone before it held.
  const probe = new Date(Date.UTC(2000, month - 1, day, 12));
  probe.setUTCFullYear(year);
  const stamp = [
    String(year).padStart(4, "0"),
    String(month).padStart(2, "0"),
    String(day).padStart(2, "0"),
  ].join("-");
  return `${stamp}T23:59:59.999${zoneOffsetAt(probe)}`;
}

/**
 * Parses a user-typed date string back to YYYY-MM-DD.
 * Returns null if the input doesn't match the expected format or is not a
 * real calendar date (e.g., Feb 30 → null).
 */
export function parseDateInput(input: string, format: DateFormat): string | null {
  if (!input) return null;
  const sep = format === "YYYY-MM-DD" ? "-" : "/";
  const parts = input.split(sep);
  if (parts.length !== 3) return null;

  let year: string, month: string, day: string;
  switch (format) {
    case "YYYY-MM-DD": [year, month, day] = parts; break;
    case "DD/MM/YYYY": [day, month, year] = parts; break;
    case "MM/DD/YYYY": [month, day, year] = parts; break;
    default: return null;
  }

  // A segment shorter than its full width is still being typed — `2026-03-1` must not
  // parse as 1 March just because the year and month already read as complete. The mask
  // never emits a segment longer than these widths, so this only ever rejects an
  // in-progress entry, never a value the field itself could have produced.
  if (year.length !== 4 || month.length !== 2 || day.length !== 2) return null;

  const y = parseInt(year, 10);
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;

  // Reject impossible calendar dates (e.g., Feb 30, Apr 31), and years 0-99 read via the
  // wrong century — see `isRealCalendarDate`.
  if (!isRealCalendarDate(y, m, d)) return null;

  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Mask a typed string into HH:MM as the user types. Strips non-digits,
 * caps at 4 digits, inserts ":" after the first two.
 */
export function maskTimeInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/**
 * True when `t` is a syntactically- and range-valid HH:MM 24h string.
 */
export function isValidTime(t: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(t)) return false;
  const [h, m] = t.split(":").map(Number);
  return h >= 0 && h < 24 && m >= 0 && m < 60;
}

/**
 * Mask a typed string into the active date format as the user types.
 * Strips non-digits, caps at 8 digits, and inserts separators at the right
 * boundaries for YYYY-MM-DD / DD/MM/YYYY / MM/DD/YYYY.
 */
export function maskDateInput(raw: string, format: DateFormat): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (format === "YYYY-MM-DD") {
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  }
  // DD/MM/YYYY and MM/DD/YYYY share the same 2/2/4 segmentation.
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Formats the time-of-day portion of a timestamp in the viewer's local timezone
 * @param date - Date string (ISO format) or Date object
 * @returns Formatted HH:MM:SS string, or "N/A" when the input is missing or unparseable
 */
export function formatTimeOfDay(date: string | Date | null | undefined): string {
  if (!date) {
    return "N/A";
  }
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) {
    return "N/A";
  }
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Formats a date string or Date object according to the specified format
 * @param date - Date string (ISO format) or Date object
 * @param format - Date format to use (YYYY-MM-DD, DD/MM/YYYY, or MM/DD/YYYY)
 * @returns Formatted date string
 */
export function formatDate(date: string | Date | null | undefined, format: DateFormat): string {
  if (!date) {
    return "N/A";
  }

  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return "N/A";
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  switch (format) {
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    case "DD/MM/YYYY":
      return `${day}/${month}/${year}`;
    case "MM/DD/YYYY":
      return `${month}/${day}/${year}`;
    default:
      return `${year}-${month}-${day}`;
  }
}

/**
 * `formatDate` plus `formatTimeOfDay`, space-separated, or "—" when there is nothing to
 * show. The one builder for a display cell that shows a moment rather than a bare date —
 * orders, pricing, and the transaction log all read a row's `created`/`expiration` through
 * this instead of composing the pair inline, so a missing value renders the same "—"
 * everywhere instead of three different guards drifting apart (one already rendered a bare
 * "N/A N/A" with no guard at all).
 *
 * The missing-value marker deliberately differs from the rest of this file: the bare-date
 * helpers above (`formatDate`, `formatDateDisplay`, `formatTimeOfDay`) render "N/A" because
 * each names one specific fact that can be absent on its own (a date, a time). This builder
 * names a moment, which reads as one fact even though it prints as two fields side by side,
 * so a page convention marks it missing with "—" instead of repeating "N/A" — the pattern
 * this repo already uses for "nothing to show" versus "this named thing is absent". The two
 * conventions are not a drift to reconcile; they answer different questions.
 */
export function formatTimestamp(
  date: string | Date | null | undefined,
  format: DateFormat,
): string {
  if (!date) return "—";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "—";
  return `${formatDate(dateObj, format)} ${formatTimeOfDay(dateObj)}`;
}

/**
 * The time-of-day part of a timestamp, as HH:MM in the viewer's timezone, or null
 * when there is nothing worth showing.
 *
 * A trade reported without a time is stored as midnight in Sao Paulo, so every
 * date-only import would otherwise render a meaningless "00:00" beside its date.
 * Midnight is therefore read as "no time was given" — a real midnight trade is not
 * a thing a venue produces, and the alternative is a column of noise.
 */
export function timeOfDayOrNull(date: string | null | undefined): string | null {
  if (!date) return null;
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return null;
  if (parsed.getHours() === 0 && parsed.getMinutes() === 0) return null;
  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Composes what `POST /external-trade` accepts for `reference_time` from the dialog's
 * two inputs. With no time the bare date is sent, which the backend reads as midnight
 * in Sao Paulo — the same instant it has always meant. With a time, the viewer's own
 * timezone resolves it, matching how every timestamp in the console is displayed.
 */
export function composeReferenceTime(datePart: string, timePart: string): string {
  if (!datePart) return "";
  if (!timePart) return datePart;
  const composed = new Date(`${datePart}T${timePart}`);
  if (isNaN(composed.getTime())) return datePart;
  return composed.toISOString();
}

/**
 * `datePart` (`YYYY-MM-DD`) as the explicit UTC instant `POST /position-parse-request`
 * expects for its request-level `reference_time` — midnight UTC of the chosen day,
 * matching the parser's own default for a request that sends none at all (parser README,
 * "Reference time": defaults to 00:00 UTC of the day the request is created).
 *
 * This is not `composeReferenceTime` with an empty time part: that form sends a bare
 * date, which `common_core.validator.datetime.Date` reads as midnight
 * `America/Sao_Paulo` — a different, also-valid encoding used by the trade/snapshot
 * dialogs' own `reference_time`, elsewhere in the console. Brazil sits at UTC-3, so that
 * reading still lands on the *same* UTC calendar day (03:00 UTC) — switching between the
 * two forms never shifts which day is recorded. The reason to send the explicit-UTC form
 * here instead is to match the parser's own literal wire example byte for byte, not to
 * avoid a day shift that was never real.
 */
export function composeReferenceTimeUtcMidnight(datePart: string): string {
  return `${datePart}T00:00:00+00:00`;
}
